import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { PencilSquareIcon } from "@heroicons/react/24/solid";

import {
  checkableTags,
  checkboxOptions,
  HeadlineForm,
} from "./models/HeadlineForm";
import { useHeadlineFormStore } from "./models/store";
import {
  useTagField,
  useHeadlineFormDefaults,
  useHeadlineSubmit,
} from "./hooks";
import { useRecommendTags } from "./api";

import SectionSelect from "./SectionSelect";
import ContentEditor from "./ContentEditor";
import AdditionalTagSearch from "./AdditionalTagSearch";
import AccordianField from "./AccordianField";
import Spinner from "../../ui-components/spinner";
import SelectableBadge from "../../ui-components/badge";
import { Button } from "@/ui-components/button-with-loading";

import type { Headline } from "../newsquawk-headlines/models/Headline";
import type { Section } from "./models/Section";
import type { Control } from "react-hook-form";

import "easymde/dist/easymde.min.css";

type CreateModeProps = {
  mode?: "create";
  headline?: never;
  onSubmitted?: () => void;
};

type EditModeProps = {
  mode: "edit";
  headline: Headline;
  onSubmitted?: () => void;
};

type PropTypes = CreateModeProps | EditModeProps;

export default function CreateHeadlineForm(props: PropTypes) {
  const { mode = "create", onSubmitted } = props;
  const isEditing = mode === "edit";
  const headline = isEditing ? props.headline : undefined;

  // Form store for cross-widget "promote to headline" feature
  const formStore = useHeadlineFormStore();

  // Compute default values from headline or promoted content
  const defaultValues = useHeadlineFormDefaults(
    headline,
    isEditing,
    formStore.headline
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<HeadlineForm>({
    mode: "onBlur",
    shouldFocusError: true,
    defaultValues,
  });

  // Tag management via useFieldArray
  const {
    tags,
    fields: tagFields,
    toggleTag,
    addSectionAsTag,
    hasTag,
    selectAll,
  } = useTagField(control);

  // Submission logic
  const { submitHeadline, isLoading } = useHeadlineSubmit({
    isEditing,
    headlineId: headline?.id,
    onSuccess: () => {
      reset({ tags: [] });
      formStore.clearHeadline();
      onSubmitted?.();
    },
  });

  // Watch content and subject for tag recommendations
  const content = useWatch({ control, name: "content" });
  const subject = useWatch({ control, name: "subject" });
  const {
    tags: recommendedTags,
    isLoading: tagsLoading,
    isSuccess: tagsLoaded,
  } = useRecommendTags(`${subject || ""} ${content || ""}`.trim(), { threshold: 0.3 });

  // Handle section change - add section as a tag
  const handleSectionChange = (section: Section) => {
    addSectionAsTag(section);
  };

  // Reset form when defaultValues change (e.g., when formStore updates)
  useEffect(() => {
    if (formStore.headline) {
      reset(defaultValues);
    }
  }, [formStore.headline, defaultValues, reset]);

  // Form submission handler
  const onSubmit = async (data: HeadlineForm) => {
    await submitHeadline(data, checkableTags);
  };

  // Check if all recommended tags are selected
  const allRecommendedTagsSelected =
    recommendedTags.length > 0 &&
    recommendedTags.every((tag) => hasTag(tag.tag_id));

  return (
    <form className="space-y-4 px-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-start">
        {/* Subject field */}
        <div className="col-span-2 gap-y-2 border-b border-gray-100/20">
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-300 relative"
          >
            Subject<span className="align-super absolute top-0 px-1">*</span>
          </label>

          <input
            id="subject"
            type="text"
            className="block min-w-0 w-full grow p-1 pr-4 text-base text-gray-900 focus:outline-none sm:text-sm/6 w-100 rounded border-none"
            {...register("subject", {
              required: "Subject value is required",
              minLength: {
                value: 5,
                message: "Subject should be at least 5 letters",
              },
              maxLength: {
                value: 255,
                message: "Subject should be at most 255 letters",
              },
            })}
          />
          {errors.subject && (
            <p className="text-red-300 text-xs/6 font-medium text-right">
              {errors.subject?.message}
            </p>
          )}
        </div>

        {/* Section field */}
        <div className="border-b border-gray-100/20">
          <div>
            <SectionSelect
              control={control as unknown as Control}
              label="Section *"
              sectionId={
                (headline as Headline | undefined)?.section_id as
                  | number
                  | undefined
              }
              onChange={handleSectionChange}
            />
          </div>

          {errors.section && (
            <p className="text-red-300 text-xs/6 font-medium text-right">
              {errors.section?.message}
            </p>
          )}
        </div>

        {/* Source field */}
        <div className="border-b border-gray-100/20">
          <label
            htmlFor="source"
            className="block text-sm font-medium text-gray-300 relative"
          >
            Source<span className="align-super absolute top-0 px-1">*</span>
          </label>
          <input
            id="source"
            type="text"
            className="block min-w-0 w-full grow p-1 pr-4 text-base text-gray-900 focus:outline-none sm:text-sm/6 w-100 rounded border-none"
            {...register("source", {
              required: "Source value is required",
              minLength: {
                value: 3,
                message: "Source should be at least 3 characters long",
              },
            })}
          />
          {errors.source && (
            <p className="text-red-300 text-xs/6 font-medium text-right">
              {errors.source?.message}
            </p>
          )}
        </div>

        {/* Published date field */}
        <div className="col-span-2 border-b border-gray-100/20">
          <label
            htmlFor="published_at"
            className="block text-sm font-medium text-gray-300"
          >
            Published at
          </label>

          <input
            id="published_at"
            type="datetime-local"
            className="block min-w-0 w-full grow p-1 pr-4 text-base text-gray-900 focus:outline-none sm:text-sm/6 rounded border-none"
            {...register("published_at")}
          />
        </div>

        {/* Content field */}
        <div className="col-span-2">
          <label
            htmlFor="content"
            className="block text-sm font-medium text-gray-300 relative"
          >
            Content <span className="align-super absolute top-0 px-1">*</span>
          </label>

          <ContentEditor
            name="content"
            control={control as unknown as Control}
          />

          {errors.content && (
            <p className="text-red-300 text-xs/6 font-medium text-right">
              {errors.content?.message}
            </p>
          )}
        </div>

        {/* Selected tags */}
        <div className="col-span-2 flex flex-wrap gap-2">
          {tagFields.map((field, index) => (
            <SelectableBadge
              key={field.id}
              text={tags[index]?.name}
              fallbackText="[Tag label not available]"
              selected
              onSelect={() => tags[index] && toggleTag(tags[index])}
            />
          ))}
        </div>

        {/* Recommended tags */}
        <div className="col-span-2">
          <span className="text-sm/8 font-medium text-gray-300 flex items-center gap-2">
            Recommended Tags
            {tagsLoading && <Spinner size="sm" />}
          </span>

          {!tagsLoading && !tagsLoaded && (
            <p className="text-xs text-gray-400 mb-2">
              Start typing to get recommended tags
            </p>
          )}

          {tagsLoaded && recommendedTags.length === 0 && (
            <p className="text-xs text-gray-400 mb-2">No tags found</p>
          )}

          {/* Display recommended tags */}
          <div className="flex flex-wrap items-center gap-2">
            {recommendedTags.length > 0 && (
              <SelectableBadge
                key="select-all-tags"
                text="Select All"
                selected={allRecommendedTagsSelected}
                onSelect={() => selectAll(recommendedTags)}
              />
            )}

            {recommendedTags.map((tag) => (
              <SelectableBadge
                key={tag.tag_id}
                text={tag.name}
                fallbackText="[Tag label not available]"
                selected={hasTag(tag.tag_id)}
                onSelect={() => toggleTag(tag)}
              />
            ))}
          </div>
        </div>

        {/* Additional Tags */}
        <AdditionalTagSearch
          onTagToggle={toggleTag}
          isTagSelected={hasTag}
          onSelectAll={selectAll}
        />

        {/* Form action button */}
        <div className="col-span-2 place-self-end gap-x-6 py-2 mb-4">
          <Button
            loading={isLoading}
            disabled={isLoading}
            size="md"
            type="submit"
            renderIcon={() =>
              isEditing ? (
                <PencilSquareIcon className="size-4" />
              ) : (
                <PlusCircleIcon className="size-4" />
              )
            }
          >
            {isEditing ? "Update Headline" : "Create Headline"}
          </Button>
        </div>

        {/* Options checkboxes */}
        <div className="col-span-2 grid grid-cols-2">
          {(
            Object.keys(checkboxOptions) as Array<keyof typeof checkboxOptions>
          ).map((option) => (
            <div className="flex items-center gap-x-4" key={option}>
              <input type="checkbox" id={option} {...register(option)} />
              <label
                htmlFor={option}
                className="block text-sm/6 font-medium text-gray-300 capitalize"
              >
                {option.replace(/_/g, " ")}
              </label>
            </div>
          ))}

          {/* Tag Checkboxes */}
          {checkableTags.map((checkbox) => (
            <div className="flex items-center gap-x-4" key={checkbox.id}>
              <input
                type="checkbox"
                id={`tagCheckboxes.${checkbox.label}`}
                {...register(`tagCheckboxes.${checkbox.label}`)}
              />
              <label
                htmlFor={`tagCheckboxes.${checkbox.label}`}
                className="block text-sm/6 font-medium text-gray-300 capitalize"
              >
                {checkbox.label}
              </label>
            </div>
          ))}
        </div>

        {/* Accordions */}
        {/* Reaction */}
        <div className="flex flex-col gap-y-2 col-span-2">
          <AccordianField
            name="reaction_details"
            control={control as unknown as Control}
            label="Reaction"
            secondaryLabel={
              headline?.reaction_updated_at
                ? `Last updated ${headline.reaction_updated_at?.text}`
                : undefined
            }
            initialValue={headline?.reaction_details as string | undefined}
            defaultOpen={isEditing}
          />
        </div>

        {/* Analysis */}
        <div className="flex flex-col gap-y-2 col-span-2">
          <AccordianField
            name="analysis_details"
            control={control as unknown as Control}
            label="Analysis"
            secondaryLabel={
              headline?.analysis_updated_at
                ? `Last updated ${headline.analysis_updated_at?.text}`
                : undefined
            }
            initialValue={headline?.analysis_details as string | undefined}
            defaultOpen={isEditing}
          />
        </div>

        {/* Linked content */}
        <div className="flex flex-col gap-y-2 col-span-2">
          <AccordianField
            name="linked_content"
            control={control as unknown as Control}
            label="Linked Content"
            initialValue={headline?.linked_content as string | undefined}
            defaultOpen={isEditing}
          />
        </div>
      </div>
    </form>
  );
}
